import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef } from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";

import { JslibModule } from "@bitwarden/angular/jslib.module";
import { PasswordStrengthV2Component } from "@bitwarden/angular/tools/password-strength/password-strength-v2.component";
import { InputPasswordComponent, PasswordCalloutComponent } from "@bitwarden/auth/angular";
import { AuditService } from "@bitwarden/common/abstractions/audit.service";
import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { MasterPasswordServiceAbstraction } from "@bitwarden/common/key-management/master-password/abstractions/master-password.service.abstraction";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { ValidationService } from "@bitwarden/common/platform/abstractions/validation.service";
import { CipherService } from "@bitwarden/common/vault/abstractions/cipher.service";
import {
  AsyncActionsModule,
  ButtonModule,
  CheckboxModule,
  DialogService,
  FormFieldModule,
  IconButtonModule,
  IconModule,
  InputModule,
  LinkModule,
  ToastService,
} from "@bitwarden/components";
import { PasswordGenerationServiceAbstraction } from "@bitwarden/generator-legacy";
import { KdfConfigService } from "@bitwarden/key-management";
import { I18nPipe } from "@bitwarden/ui-common";

import { BvRotateEncryptionKeyDialogComponent } from "./bv-rotate-encryption-key-dialog.component";

// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: "app-web-input-password",
  templateUrl: "./web-input-password.component.html",
  imports: [
    CommonModule,
    AsyncActionsModule,
    ButtonModule,
    CheckboxModule,
    FormFieldModule,
    IconButtonModule,
    IconModule,
    InputModule,
    JslibModule,
    PasswordCalloutComponent,
    PasswordStrengthV2Component,
    ReactiveFormsModule,
    LinkModule,
    I18nPipe,
  ],
})
export class WebInputPasswordComponent extends InputPasswordComponent {
  constructor(
    auditService: AuditService,
    private readonly injectedCipherService: CipherService,
    cdr: ChangeDetectorRef,
    private readonly injectedDialogService: DialogService,
    destroyRef: DestroyRef,
    formBuilder: FormBuilder,
    private readonly injectedI18nService: I18nService,
    kdfConfigService: KdfConfigService,
    masterPasswordService: MasterPasswordServiceAbstraction,
    passwordGenerationService: PasswordGenerationServiceAbstraction,
    private readonly injectedPlatformUtilsService: PlatformUtilsService,
    policyService: PolicyService,
    toastService: ToastService,
    validationService: ValidationService,
  ) {
    super(
      auditService,
      injectedCipherService,
      cdr,
      injectedDialogService,
      destroyRef,
      formBuilder,
      injectedI18nService,
      kdfConfigService,
      masterPasswordService,
      passwordGenerationService,
      injectedPlatformUtilsService,
      policyService,
      toastService,
      validationService,
    );
  }

  protected override async rotateUserKeyClicked() {
    const rotateUserKeyCtrl = this.formGroup.controls.rotateUserKey;
    const rotateUserKey = rotateUserKeyCtrl?.value;

    if (!rotateUserKey) {
      return;
    }

    if (!this.userId) {
      throw new Error("userId not passed down");
    }

    const ciphers = await this.injectedCipherService.getAllDecrypted(this.userId);

    let hasOldAttachments = false;

    if (ciphers != null) {
      for (let i = 0; i < ciphers.length; i++) {
        if (ciphers[i].organizationId == null && ciphers[i].hasOldAttachments) {
          hasOldAttachments = true;
          break;
        }
      }
    }

    if (hasOldAttachments) {
      const learnMore = await this.injectedDialogService.openSimpleDialog({
        title: { key: "warning" },
        content: { key: "oldAttachmentsNeedFixDesc" },
        acceptButtonText: { key: "learnMore" },
        cancelButtonText: { key: "close" },
        type: "warning",
      });

      if (learnMore) {
        this.injectedPlatformUtilsService.launchUri(
          "https://bitwarden.com/help/attachments/#add-storage-space",
        );
      }

      rotateUserKeyCtrl.setValue(false);
      return;
    }

    const result = await BvRotateEncryptionKeyDialogComponent.open(
      this.injectedDialogService,
      this.injectedI18nService,
    );

    if (!result) {
      rotateUserKeyCtrl.setValue(false);
    }
  }
}
